import { cn } from '@plato/ui/util/classes';
import { useEffect, useRef, useState, type CSSProperties, type ReactElement, type RefObject } from 'react';
import {
    leftEyeRest,
    leftSpiralCenter,
    mouthRest,
    mouthShapes,
    rightEyeRest,
    rightSpiralCenter,
    speechSequence,
    spiral,
    spiralStrokeWidth,
} from './plato-bust-shapes.ts';

export type PlatoBustProps = {
    /** Cycles the mouth through speech shapes while the moustache breathes. */
    speaking: boolean;
    /** Random blinking. The bust also blinks on its own while a pointer is near. */
    blink: boolean;
    /** Head, face and eyes turn toward a fine pointer within `followRadius`. */
    follow: boolean;
    /** How far beyond the bust's edge, in px, a pointer is still followed. */
    followRadius?: number;
    className?: string;
};

const zoom = 1.1;
const strokeWidth = 14.5;

/** With reduced motion the bust stays in its rest pose. */
const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const clamp = (value: number): number => Math.max(-1, Math.min(1, value));

/** Left-right crossings of the bust's centre within the window that make it dizzy. */
const dizzyCrossings = 8;
const dizzyWindowMs = 5000;
const dizzyDurationMs = 1000;

/**
 * How each part answers the pointer. `useBustFollow` writes the offset as
 * `--fx`/`--fy` on the root and the lagging `--tx`/`--ty` the curls swing on;
 * unset — follow off, coarse pointer, reduced motion — every part sits at rest.
 */
const followTransform = {
    body: 'translate(calc(var(--fx, 0) * 3px), calc(var(--fy, 0) * 2px))',
    head: `translate(calc(var(--fx, 0) * 10px), calc(var(--fy, 0) * 7px))
           rotate(calc(var(--fx, 0) * 3deg)) skewX(calc(var(--fx, 0) * -3deg))`,
    face: 'translate(calc(var(--fx, 0) * 9px), calc(var(--fy, 0) * 7px))',
    eyes: 'translate(calc(var(--fx, 0) * 14px), calc(var(--fy, 0) * 4px))',
    curls: `translate(calc((var(--tx, 0) - var(--fx, 0)) * 12px + var(--tx, 0) * 3px),
                      calc((var(--ty, 0) - var(--fy, 0)) * 8px))`,
};

/** Percentage origins measured against the part's own bounding box. */
const part = (transformOrigin: string, transform?: string): CSSProperties => ({
    transformBox: 'fill-box',
    transformOrigin,
    transform,
});
/** The spiral is drawn around 0 0 and placed by its parent group, so it spins in place. */
const spinAroundOrigin: CSSProperties = { transformBox: 'view-box', transformOrigin: '0 0' };

/**
 * The animated line-art bust of Plato behind the Ask Plato triggers. The moving
 * parts run on refs, CSS variables and the Web Animations API, so nothing
 * re-renders per frame — see `plato-bust-motion.ts`.
 */
export function PlatoBust({
    speaking,
    blink,
    follow,
    followRadius = 100,
    className,
}: PlatoBustProps): ReactElement {
    const rootRef = useRef<HTMLSpanElement>(null);
    const eyesRef = useRef<SVGGElement>(null);
    const mouthRef = useRef<SVGPathElement>(null);
    const leftMoustacheRef = useRef<SVGPathElement>(null);
    const rightMoustacheRef = useRef<SVGPathElement>(null);
    const leftSpiralRef = useRef<SVGPathElement>(null);
    const rightSpiralRef = useRef<SVGPathElement>(null);

    const { pointerNear, dizzy } = useBustFollow(
        { root: rootRef, leftSpiral: leftSpiralRef, rightSpiral: rightSpiralRef },
        { enabled: follow, radius: followRadius },
    );
    useBustBlink(eyesRef, blink || pointerNear);
    useBustSpeech(
        { mouth: mouthRef, leftMoustache: leftMoustacheRef, rightMoustache: rightMoustacheRef },
        speaking,
    );

    return (
        <span
            ref={rootRef}
            aria-hidden
            className={cn(
                'grid size-full place-items-center overflow-hidden rounded-full bg-container text-content',
                className,
            )}
        >
            <svg
                viewBox="0 0 548 498"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={strokeWidth}
                className="h-auto w-full overflow-visible"
                style={{ transform: `scale(${zoom})`, transformOrigin: '50% 42%' }}
            >
                <g style={part('50% 100%', followTransform.body)}>
                    <path d="m80.39 491.27c-8.76-136.91 44.3-132.65 103.36-160.93" />
                    <path d="m460.02 484.93c6.32-128.67-51.31-136.76-107.16-160.7" />
                    <path d="m195.1 478.89c81.61-35.93 133.25-43.78 188.93-141.34" />
                    <path d="m323.59 487c38.55-27.86 74.72-68.07 89.94-129.94" />
                </g>
                <g style={part('50% 85%', followTransform.head)}>
                    <path d="m348.25 124.95c0.25 22.25 11.5 23 21.25 26.5-20.32-2.06-10.85 27.25-0.49 42.66-6 0.25-10.51 8.09-10.26 16.59 0.25 8.5 1.75 11.25-1.75 24.5 10.25 21 8.25 49-5.5 59.75 2.5 34.25-16 71.25-47.75 75.75" />
                    <path d="m177.25 196.21c-0.71 9.99 0 28.55 4.52 37.59-18.8 27.61-11.66 49.97 3.33 62.82-9.52 58.54 33.55 73.05 49.73 76.86" />
                    <path d="m237.69 351.83c-3.69 71.38 75.43 59.25 63.53-2.14" />
                    <g style={part('50% 50%', followTransform.face)}>
                        <path ref={mouthRef} d={mouthRest} style={part('50% 50%')} />
                        <path
                            ref={leftMoustacheRef}
                            d="m224.84 278.3c6.72-16.04 14.7-14.99 29.99-15.04"
                            style={part('100% 50%')}
                        />
                        <path
                            ref={rightMoustacheRef}
                            d="m284.62 263.01c18.52-1.75 23.27 5.26 28.03 14.27"
                            style={part('0% 50%')}
                        />
                        <path d="m262.2 247.61c6.66 12.37 17.61 3.56 17.61-4.29 0-7.85-6.43-46.64 0-53.77" />
                        {/* While dizzy the spirals stand in for both the eyes and the brows. */}
                        <g visibility={dizzy ? 'hidden' : undefined}>
                            <path d="m219.11 188.15c12.14-3.03 21.13-3.88 31.6-1.26" />
                            <path d="m323.59 188.59c-10.47-8.09-35.46-5.95-42.59-0.95" />
                        </g>
                        <g style={{ transform: followTransform.eyes }}>
                            <g
                                ref={eyesRef}
                                fill="currentColor"
                                stroke="none"
                                visibility={dizzy ? 'hidden' : undefined}
                                style={part('50% 50%')}
                            >
                                <path d={leftEyeRest} />
                                <path d={rightEyeRest} />
                            </g>
                            {dizzy && (
                                <g strokeWidth={spiralStrokeWidth}>
                                    <g transform={`translate(${leftSpiralCenter.x} ${leftSpiralCenter.y})`}>
                                        <path ref={leftSpiralRef} d={spiral} style={spinAroundOrigin} />
                                    </g>
                                    <g transform={`translate(${rightSpiralCenter.x} ${rightSpiralCenter.y})`}>
                                        <path ref={rightSpiralRef} d={spiral} style={spinAroundOrigin} />
                                    </g>
                                </g>
                            )}
                        </g>
                    </g>
                    <g style={part('50% 50%', followTransform.curls)}>
                        <path d="m199.25 114.2c23 22.5 49.5 6.5 58.75-21.25" />
                        <path d="m277.25 104.95c23.25 15.31 44 14 62-7.75" />
                        <path d="m194.14 133.15c-6.18 6.66-1.66 15.47-6.66 27.37-2.51 5.98-7.38 14.27-16.66 17.13" />
                    </g>
                    <path d="m176.25 240.95c-13.25-5-26.75-14-23.75-25.75-25-21.75-28.25-50.25-6.25-65-11.5-38.25 8-58.75 38-60 11-37 46-35.25 57.5-31.75 25.25-24.25 53.75-16 61.5-2 29.25-6.25 52.75 16.5 53.25 31.25 33.5 9.75 42.5 28.75 35 58 25 17.75 7.75 61.5-3.5 63.75 3 21.75-21.25 26.6-28 30.05" />
                </g>
            </svg>
        </span>
    );
}

type FollowRefs = {
    root: RefObject<HTMLElement | null>;
    leftSpiral: RefObject<SVGPathElement | null>;
    rightSpiral: RefObject<SVGPathElement | null>;
};

type FollowOptions = {
    /** Off for the mobile trigger; also ignored for coarse pointers and reduced motion. */
    enabled: boolean;
    /** How far beyond the bust's edge, in px, a pointer is still followed. */
    radius: number;
};

type FollowState = {
    /** A fine pointer is within reach, so the bust is looking over. */
    pointerNear: boolean;
    /** Circled too fast: spiral eyes for a moment. */
    dizzy: boolean;
};

/**
 * Eases the pointer offset onto the root element as `--fx`/`--fy` (-1..1), plus
 * the lagging `--tx`/`--ty` the curls swing on; `followTransform` in the markup
 * decides how far each part moves. Nothing re-renders per frame, and the loop
 * only runs while the pointer is nearby.
 */
function useBustFollow(
    { root: rootRef, leftSpiral, rightSpiral }: FollowRefs,
    { enabled, radius }: FollowOptions,
): FollowState {
    const [pointerNear, setPointerNear] = useState(false);
    const [dizzy, setDizzy] = useState(false);

    useEffect(() => {
        const root = rootRef.current;
        if (!enabled || !root || !window.matchMedia('(pointer: fine)').matches || reducedMotion()) {
            return;
        }

        const target = { x: 0, y: 0 };
        const current = { x: 0, y: 0 };
        const trailing = { x: 0, y: 0 };
        let frame = 0;
        let near = false;
        let wasLeftOfCentre: boolean | undefined;
        const crossings: number[] = [];
        let dizzyTimer: ReturnType<typeof setTimeout> | undefined;

        const write = (): void => {
            root.style.setProperty('--fx', String(current.x));
            root.style.setProperty('--fy', String(current.y));
            root.style.setProperty('--tx', String(trailing.x));
            root.style.setProperty('--ty', String(trailing.y));
        };
        const settled = (): boolean =>
            Math.abs(target.x - current.x) < 0.001 &&
            Math.abs(target.y - current.y) < 0.001 &&
            Math.abs(current.x - trailing.x) < 0.001 &&
            Math.abs(current.y - trailing.y) < 0.001;
        const step = (): void => {
            current.x += (target.x - current.x) * 0.1;
            current.y += (target.y - current.y) * 0.1;
            trailing.x += (current.x - trailing.x) * 0.06;
            trailing.y += (current.y - trailing.y) * 0.06;
            if (settled()) {
                Object.assign(current, target);
                Object.assign(trailing, target);
                frame = 0;
            } else {
                frame = requestAnimationFrame(step);
            }
            write();
        };
        const run = (): void => {
            if (frame === 0) {
                frame = requestAnimationFrame(step);
            }
        };

        const setNear = (next: boolean): void => {
            if (near !== next) {
                near = next;
                setPointerNear(next);
            }
        };
        const rest = (): void => {
            target.x = 0;
            target.y = 0;
            wasLeftOfCentre = undefined;
            setNear(false);
            run();
        };

        /** Crossing the centre often enough inside the window sets the bust spinning. */
        const trackCrossing = (offsetX: number): void => {
            const leftOfCentre = offsetX < 0;
            const crossed = wasLeftOfCentre !== undefined && wasLeftOfCentre !== leftOfCentre;
            wasLeftOfCentre = leftOfCentre;
            if (!crossed || dizzyTimer !== undefined) {
                return;
            }
            const now = performance.now();
            crossings.push(now);
            while (now - (crossings[0] ?? now) > dizzyWindowMs) {
                crossings.shift();
            }
            if (crossings.length < dizzyCrossings) {
                return;
            }
            crossings.length = 0;
            setDizzy(true);
            dizzyTimer = setTimeout(() => {
                dizzyTimer = undefined;
                setDizzy(false);
            }, dizzyDurationMs);
        };

        // The bust is fixed in the viewport: read its box once, again after a resize.
        let rect: DOMRect | undefined;
        const invalidateRect = (): void => {
            rect = undefined;
        };
        const onPointerMove = (event: PointerEvent): void => {
            rect ??= root.getBoundingClientRect();
            // Hidden below the `sm` breakpoint: a zero-size box has no centre to
            // measure against, and dividing by its width would poison the easing.
            if (rect.width === 0) {
                rect = undefined;
                if (near) {
                    rest();
                }
                return;
            }
            const offset = {
                x: event.clientX - (rect.left + rect.width / 2),
                y: event.clientY - (rect.top + rect.height / 2),
            };
            const reach = rect.width / 2 + radius;
            // Far away is the common case: a couple of comparisons and out.
            if (
                Math.abs(offset.x) > reach ||
                Math.abs(offset.y) > reach ||
                Math.hypot(offset.x, offset.y) > reach
            ) {
                if (near) {
                    rest();
                }
                return;
            }
            setNear(true);
            trackCrossing(offset.x);
            target.x = clamp(offset.x / (rect.width * 0.9));
            target.y = clamp((offset.y + rect.height * 0.05) / (rect.height * 0.9));
            run();
        };

        window.addEventListener('pointermove', onPointerMove, { passive: true });
        window.addEventListener('resize', invalidateRect);
        document.addEventListener('mouseleave', rest);
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('resize', invalidateRect);
            document.removeEventListener('mouseleave', rest);
            cancelAnimationFrame(frame);
            clearTimeout(dizzyTimer);
            for (const name of ['--fx', '--fy', '--tx', '--ty']) {
                root.style.removeProperty(name);
            }
            setPointerNear(false);
            setDizzy(false);
        };
    }, [enabled, radius, rootRef]);

    useEffect(() => {
        if (!dizzy) {
            return;
        }
        const timing: KeyframeAnimationOptions = { duration: dizzyDurationMs, easing: 'ease-in-out' };
        const spin = (degrees: number): Keyframe[] => [
            { transform: 'rotate(0deg)' },
            { transform: `rotate(${degrees}deg)` },
        ];
        leftSpiral.current?.animate(spin(720), timing);
        rightSpiral.current?.animate(spin(-720), timing);
    }, [dizzy, leftSpiral, rightSpiral]);

    return { pointerNear, dizzy };
}

/** Blinks at a lazy random interval, doubling up every so often. */
function useBustBlink(eyesRef: RefObject<SVGGElement | null>, active: boolean): void {
    useEffect(() => {
        const eyes = eyesRef.current;
        if (!active || !eyes || reducedMotion()) {
            return;
        }
        let timer: ReturnType<typeof setTimeout> | undefined;
        let doubleTimer: ReturnType<typeof setTimeout> | undefined;
        const blinkOnce = (): void => {
            eyes.animate(
                [{ transform: 'scaleY(1)' }, { transform: 'scaleY(0.06)' }, { transform: 'scaleY(1)' }],
                { duration: 170, easing: 'ease-in-out' },
            );
        };
        const schedule = (): void => {
            timer = setTimeout(
                () => {
                    blinkOnce();
                    if (Math.random() < 0.22) {
                        doubleTimer = setTimeout(blinkOnce, 260);
                    }
                    schedule();
                },
                2200 + Math.random() * 3800,
            );
        };
        schedule();
        return () => {
            clearTimeout(timer);
            clearTimeout(doubleTimer);
        };
    }, [active, eyesRef]);
}

type SpeechRefs = {
    mouth: RefObject<SVGPathElement | null>;
    leftMoustache: RefObject<SVGPathElement | null>;
    rightMoustache: RefObject<SVGPathElement | null>;
};

/** Cycles the mouth through speech shapes while the moustache breathes. */
function useBustSpeech(
    { mouth: mouthRef, leftMoustache, rightMoustache }: SpeechRefs,
    speaking: boolean,
): void {
    useEffect(() => {
        const mouth = mouthRef.current;
        const left = leftMoustache.current;
        const right = rightMoustache.current;
        if (!speaking || !mouth || !left || !right || reducedMotion()) {
            return;
        }
        let index = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const tick = (): void => {
            mouth.setAttribute('d', mouthShapes[speechSequence[index++ % speechSequence.length] ?? 'rest']);
            timer = setTimeout(tick, 90 + Math.random() * 110);
        };
        tick();
        const breath: Keyframe[] = [{ transform: 'translateY(0)' }, { transform: 'translateY(-2.5px)' }];
        const timing: KeyframeAnimationOptions = {
            duration: 240,
            easing: 'ease-in-out',
            iterations: Infinity,
        };
        const leftBreath = left.animate(breath, { ...timing, direction: 'alternate' });
        const rightBreath = right.animate(breath, { ...timing, direction: 'alternate-reverse' });
        return () => {
            clearTimeout(timer);
            leftBreath.cancel();
            rightBreath.cancel();
            mouth.setAttribute('d', mouthRest);
        };
    }, [leftMoustache, mouthRef, rightMoustache, speaking]);
}
